import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { ChevronRightIcon, TagIcon } from 'lucide-react'
import { useEnumsQuery } from '@/api/accommodation/hooks'
import { trackSearchLabelFilterSelected } from '@/lib/track'
import { useSearchFilters } from './useSearchFilters'

const ALL_VALUE = '0'

function LabelFilterContent({ onOpenChange }: { onOpenChange?: (open: boolean) => void }) {
  const { localFilters, updateLocalFilters } = useSearchFilters()
  const { data: enums } = useEnumsQuery()

  const handleChange = (value: string) => {
    const labelId = value === ALL_VALUE ? undefined : Number(value)
    const labelName = enums?.labels.find((l) => l.id === labelId)?.name
    updateLocalFilters({ labelId })
    trackSearchLabelFilterSelected(labelId, labelName)
  }

  return (
    <Select
      name="labelId"
      value={localFilters.labelId?.toString() ?? ALL_VALUE}
      onValueChange={handleChange}
      onOpenChange={onOpenChange}
    >
      <SelectTrigger id="labelId" className="h-10! w-full bg-slate-100 text-slate-900!">
        <div className="flex items-center gap-2">
          <TagIcon className="text-red h-4 w-4" />
          <SelectValue />
        </div>
      </SelectTrigger>
      <SelectContent className="max-h-[40vh]">
        <SelectItem value={ALL_VALUE}>Vše</SelectItem>
        {enums?.labels
          .slice()
          .sort((a, b) => a.name.localeCompare(b.name, 'cs'))
          .map((label) => (
            <SelectItem key={label.id} value={label.id.toString()}>
              {label.name}
            </SelectItem>
          ))}
      </SelectContent>
    </Select>
  )
}

interface LabelFilterMobileProps {
  onOpenLabelSelection?: () => void
}

export function LabelFilterMobile({ onOpenLabelSelection }: LabelFilterMobileProps) {
  const { localFilters } = useSearchFilters()
  const { data: enums } = useEnumsQuery()

  const selectedName =
    localFilters.labelId === undefined
      ? 'Vše'
      : (enums?.labels.find((l) => l.id === localFilters.labelId)?.name ?? 'Vše')

  return (
    <div className="flex items-center justify-between">
      <span className="text-sm font-medium text-slate-800">Typ pobytu</span>
      <Button
        variant="ghost"
        className="flex h-auto items-center gap-1 font-normal text-slate-600"
        onClick={onOpenLabelSelection}
      >
        {selectedName}
        <ChevronRightIcon className="mt-1" />
      </Button>
    </div>
  )
}

interface LabelSelectionMobileProps {
  onSelect: () => void
}

export function LabelSelectionMobile({ onSelect }: LabelSelectionMobileProps) {
  const { localFilters, updateLocalFilters } = useSearchFilters()
  const { data: enums } = useEnumsQuery()

  const handleSelect = (labelId: number | undefined, labelName: string | undefined) => {
    updateLocalFilters({ labelId })
    trackSearchLabelFilterSelected(labelId, labelName)
    onSelect()
  }

  const labels = [
    { id: undefined, name: 'Vše' },
    ...(enums?.labels
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name, 'cs'))
      .map((l) => ({ id: l.id, name: l.name })) ?? []),
  ]

  return (
    <div className="flex max-h-[60vh] flex-col gap-1 overflow-y-auto">
      {labels.map((label) => (
        <Button
          key={label.id ?? 'all'}
          variant="ghost"
          className="-ml-2 justify-between px-2 text-left text-lg text-slate-800"
          onClick={() => handleSelect(label.id, label.name)}
        >
          <span className={localFilters.labelId === label.id ? 'font-semibold' : 'font-normal'}>{label.name}</span>
        </Button>
      ))}
    </div>
  )
}

export function LabelFilterDesktop() {
  const { commitFilters } = useSearchFilters()

  return (
    <div className="flex grow basis-0 flex-col gap-1">
      <label htmlFor="labelId" className="text-sm text-slate-400">
        Typ pobytu
      </label>
      <LabelFilterContent
        onOpenChange={(open) => {
          if (!open) commitFilters()
        }}
      />
    </div>
  )
}
